import { readDirectBuilderSource } from './studyAuthoringBootstrap'
import { captureAuthenticatedShopBootstrap } from '../../authenticatedShopBootstrap'
import { shopifyApiFetch } from '../../shopifyApiFetch'
import { studyBuilderInitialStateQueryKey } from '../../queries/studyQueries'
import type { ShopifyAudienceMode } from '../../types'

export const DEFAULT_AUDIENCE_MODE: ShopifyAudienceMode = 'custom_merchant_segment'

/** Builds the shop-scoped bootstrap key so server-staged drafts can invalidate the active builder state. */
export function buildStudyBuilderInitialStateQueryKey(
  shopDomain: string,
  orgId: string | null = null,
  connectionId: string | null = null,
) {
  return studyBuilderInitialStateQueryKey(shopDomain, orgId, connectionId)
}

/**
 * Fetches builder options and the direct study source only. Legacy setup/draft identities are rejected before network access.
 */
export async function fetchInitialBuilderState(
  shopify: Parameters<typeof shopifyApiFetch>[0],
  signal: AbortSignal,
  editSetupId?: string | null,
  expectedShop?: string | null,
  draftId?: string | null,
  direct?: { studyId?: string; isNew: boolean },
) {
  // The scope guard stays outside the reporting boundary below: a racing shop change
  // must reject before any authenticated request — including the diagnostic report — is sent.
  // A retired setup/draft link is a browser-only refusal that never reaches the Worker: it carries no wording of its
  // own; `pageErrorFrom` names it and the Worker's load-error words (on the connected status) supply the text.
  if (editSetupId || !direct || (!direct.studyId && (!direct.isNew || !draftId?.trim())))
    throw new Error('STUDY_BUILDER_LEGACY_ROUTE_RETIRED: legacy setup link is no longer supported')
  const bootstrap = await captureAuthenticatedShopBootstrap(shopify, { expectedShop, signal })
  // The body keeps its indentation (whitespace gate). No failure is reported here: the page lifecycle reports each source failure once.
  return (async () => {
  const verifiedShop = bootstrap.shopDomain
  const authenticatedShopify = bootstrap.authenticatedShopify
  // The form's tips and starting market come in the server's first answer to the form, not a separate request.
  const directSource = await readDirectBuilderSource(
      authenticatedShopify,
      { studyId: direct.studyId, creationIntentId: direct.isNew ? draftId ?? undefined : undefined },
      signal,
    )
  return {
    authoring: directSource?.authoring ?? null, deletion: directSource?.deletion ?? null, direct: true,
    // The Worker-built starting draft for a new creation intent; null for a saved study or a cleanup receipt.
    startingDraft: directSource?.startingDraft ?? null,
    shopName: verifiedShop,
    verifiedShop,
  }
  })()
}
